package features.schedule.handlers

import kotlinx.browser.window
import shared.utils.TimeUtils

/**
 * Excel Shift Schedule Export Handler
 * Generates professional Excel file with shift calendar and member data
 */

data class ShiftScheduleData(
    val name: String,
    val email: String,
    val role: String,
    val schedule: Map<Int, String>
)

data class MemberDataRecord(
    val name: String,
    val email: String,
    val role: String,
    val timeoff: List<Map<String, String>>,
    val overtime: List<Map<String, String>>,
    val attendance: List<Map<String, String>>
)

fun exportShiftScheduleToExcel(
    teamName: String,
    members: List<MemberDataRecord>,
    month: Int = (js("new Date().getMonth()") as Int),
    year: Int = (js("new Date().getFullYear()") as Int)
) {
    console.log("📊 Exporting shift schedule to Excel...")
    
    // Get ExcelJS from window
    val ExcelJS = js("window.ExcelJS")
    if (ExcelJS == undefined) {
        window.alert("❌ ExcelJS not loaded! Please refresh page.")
        return
    }

    val workbook = js("new ExcelJS.Workbook()")
    val ws = workbook.addWorksheet("${getMonthName(month)}-$year")

    // Color scheme
    val COLORS = js("""{
        MOR: "FFD700",
        AFT: "FF8C00",
        NIG: "4169E1",
        HEADER: "2C3E50",
        TEXT_W: "FFFFFF",
        TEXT_B: "000000",
        EMPTY: "F8F9FA",
        TIMEOFF: "E74C3C",
        OVERTIME: "F39C12",
        ATTENDANCE: "27AE60"
    }""")

    var row = 1

    // ===== SHIFT TEMPLATES (Column L=12) =====
    ws.mergeCells(row, 12, row, 14)
    styleCell(ws.getCell(row, 12), COLORS.HEADER, COLORS.TEXT_W, true, 11)
    ws.getCell(row, 12).value = "SHIFT TEMPLATES"
    row++

    styleCell(ws.getCell(row, 12), COLORS.HEADER, COLORS.TEXT_W, true, 9)
    ws.getCell(row, 12).value = "Code"
    styleCell(ws.getCell(row, 13), COLORS.HEADER, COLORS.TEXT_W, true, 9)
    ws.getCell(row, 13).value = "Name"
    styleCell(ws.getCell(row, 14), COLORS.HEADER, COLORS.TEXT_W, true, 9)
    ws.getCell(row, 14).value = "Time"
    row++

    val shifts = arrayOf(
        arrayOf("MOR", "Morning", "06:00-14:00", COLORS.MOR),
        arrayOf("AFT", "Afternoon", "14:00-22:00", COLORS.AFT),
        arrayOf("NIG", "Night", "22:00-06:00", COLORS.NIG)
    )

    for (shift in shifts) {
        styleCell(ws.getCell(row, 12), shift[3] as String, COLORS.TEXT_B, true, 9)
        ws.getCell(row, 12).value = shift[0]
        styleCell(ws.getCell(row, 13), shift[3] as String, COLORS.TEXT_B, false, 9)
        ws.getCell(row, 13).value = shift[1]
        styleCell(ws.getCell(row, 14), shift[3] as String, COLORS.TEXT_B, false, 9)
        ws.getCell(row, 14).value = shift[2]
        row++
    }

    val templateEnd = row

    // ===== CALENDAR (Column O=15, P=16, Q=17, then days) =====
    var calRow = 1
    val numDays = getDaysInMonth(year, month)

    styleCell(ws.getCell(calRow, 15), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(calRow, 15).value = "Name"
    styleCell(ws.getCell(calRow, 16), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(calRow, 16).value = "Email"
    styleCell(ws.getCell(calRow, 17), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(calRow, 17).value = "Role"

    // Day columns (R=18, S=19, etc)
    for (day in 1..numDays) {
        val col = 17 + day
        styleCell(ws.getCell(calRow, col), COLORS.HEADER, COLORS.TEXT_W, true, 9)
        ws.getCell(calRow, col).value = day
    }
    calRow++

    // Sample users with sample schedule
    val sampleUsers = arrayOf(
        object {
            val name = "Godirs"
            val email = "godirs@example.com"
            val role = "Owner"
            val schedule = mapOf(
                1 to "MOR", 2 to "MOR", 3 to "MOR", 4 to "MOR", 5 to "MOR",
                8 to "AFT", 9 to "AFT", 10 to "AFT", 11 to "AFT", 12 to "AFT",
                15 to "MOR", 16 to "MOR", 17 to "MOR", 18 to "MOR", 19 to "MOR"
            )
        },
        object {
            val name = "Muslikhul Adib"
            val email = "adib@example.com"
            val role = "Admin"
            val schedule = mapOf(
                1 to "AFT", 2 to "AFT", 3 to "AFT", 4 to "AFT", 5 to "AFT",
                8 to "NIG", 9 to "NIG", 10 to "NIG", 11 to "NIG", 12 to "NIG",
                15 to "AFT", 16 to "AFT", 17 to "AFT", 18 to "AFT", 19 to "AFT"
            )
        },
        object {
            val name = "Ahmad"
            val email = "ahmad@example.com"
            val role = "Member"
            val schedule = mapOf(
                1 to "NIG", 2 to "NIG", 3 to "NIG", 4 to "NIG", 5 to "NIG",
                8 to "MOR", 9 to "MOR", 10 to "MOR", 11 to "MOR", 12 to "MOR",
                15 to "NIG", 16 to "NIG", 17 to "NIG", 18 to "NIG", 19 to "NIG"
            )
        }
    )

    for (user in sampleUsers) {
        val userData = user.asDynamic()
        ws.getCell(calRow, 15).value = userData.name
        ws.getCell(calRow, 16).value = userData.email
        ws.getCell(calRow, 17).value = userData.role

        for (day in 1..numDays) {
            val col = 17 + day
            val shift = userData.schedule[day]
            if (shift != null) {
                styleCell(ws.getCell(calRow, col), COLORS.asDynamic()[shift], COLORS.TEXT_B, true, 9)
                ws.getCell(calRow, col).value = shift
            } else {
                ws.getCell(calRow, col).value = "-"
                ws.getCell(calRow, col).font = js("{ color: { argb: 'CCCCCC' } }")
            }
        }
        calRow++
    }

    val calendarEndRow = calRow

    // ===== EMPTY SPACE (A-I) =====
    val endRow = kotlin.math.max(templateEnd, calendarEndRow)
    ws.mergeCells(1, 1, endRow, 9)
    styleCell(ws.getCell(1, 1), COLORS.EMPTY, COLORS.TEXT_B, false)
    ws.getCell(1, 1).value = ""

    // ===== MEMBER DATA SECTION =====
    val mdRow = endRow + 2
    var row2 = mdRow

    ws.mergeCells(row2, 1, row2, 9)
    styleCell(ws.getCell(row2, 1), COLORS.HEADER, COLORS.TEXT_W, true, 12)
    ws.getCell(row2, 1).value = "MEMBER DATA"
    row2++

    // Headers
    styleCell(ws.getCell(row2, 1), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(row2, 1).value = "Name"
    styleCell(ws.getCell(row2, 2), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(row2, 2).value = "Email"
    styleCell(ws.getCell(row2, 3), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(row2, 3).value = "Role"
    row2++

    // Sample member data with different cases
    val memberDataList = arrayOf(
        object {
            val name = "Godirs"
            val email = "godirs@example.com"
            val role = "Owner"
            val timeoff = arrayOf(
                mapOf("date" to "2026-01-15", "period" to "2026-01-15 to 17", "days" to "3 days", "status" to "Approved", "category" to "Annual Leave")
            )
            val overtime = arrayOf(
                mapOf("date" to "2026-01-10", "start" to "18:00", "end" to "20:00", "hours" to "2h", "status" to "Approved")
            )
            val attendance = arrayOf(
                mapOf("date" to "2026-01-02", "in" to "08:30", "out" to "17:00", "hours" to "8.5h", "status" to "Present"),
                mapOf("date" to "2026-01-03", "in" to "08:45", "out" to "17:15", "hours" to "8.5h", "status" to "Present")
            )
        },
        object {
            val name = "Muslikhul Adib"
            val email = "adib@example.com"
            val role = "Admin"
            val timeoff = arrayOf<Map<String, String>>() // Empty
            val overtime = arrayOf(
                mapOf("date" to "2026-01-05", "start" to "17:00", "end" to "19:00", "hours" to "2h", "status" to "Approved")
            )
            val attendance = arrayOf(
                mapOf("date" to "2026-01-02", "in" to "09:00", "out" to "17:30", "hours" to "8.5h", "status" to "Present"),
                mapOf("date" to "2026-01-04", "in" to "08:30", "out" to "17:00", "hours" to "8.5h", "status" to "Present")
            )
        },
        object {
            val name = "Ahmad"
            val email = "ahmad@example.com"
            val role = "Member"
            val timeoff = arrayOf(
                mapOf("date" to "2026-01-08", "period" to "2026-01-08 to 10", "days" to "3 days", "status" to "Pending", "category" to "Sick Leave")
            )
            val overtime = arrayOf<Map<String, String>>() // Empty
            val attendance = arrayOf(
                mapOf("date" to "2026-01-02", "in" to "07:30", "out" to "16:00", "hours" to "8.5h", "status" to "Present")
            )
        }
    )
    for (member in memberDataList) {
        val mData = member.asDynamic()
        val mdStartRow = row2

        ws.getCell(row2, 1).value = mData.name
        ws.getCell(row2, 2).value = mData.email
        ws.getCell(row2, 3).value = mData.role

        // TIME OFF
        if (mData.timeoff.length > 0) {
            styleCell(ws.getCell(row2, 4), COLORS.TIMEOFF, COLORS.TEXT_W, true)
            ws.getCell(row2, 4).value = "✈️ TIME OFF"
            row2++

            arrayOf("Date", "Period", "Days", "Status", "Category").forEachIndexed { i, h ->
                styleCell(ws.getCell(row2, 4 + i), COLORS.TIMEOFF, COLORS.TEXT_W, true, 9)
                ws.getCell(row2, 4 + i).value = h
            }
            row2++

            for (data in mData.timeoff) {
                ws.getCell(row2, 4).value = data["date"]
                ws.getCell(row2, 5).value = data["period"]
                ws.getCell(row2, 6).value = data["days"]
                ws.getCell(row2, 7).value = data["status"]
                ws.getCell(row2, 8).value = data["category"]
                row2++
            }
        }

        // OVERTIME
        if (mData.overtime.length > 0) {
            styleCell(ws.getCell(row2, 4), COLORS.OVERTIME, COLORS.TEXT_W, true)
            ws.getCell(row2, 4).value = "⏱️ OVERTIME"
            row2++

            arrayOf("Date", "Start", "End", "Hours", "Status").forEachIndexed { i, h ->
                styleCell(ws.getCell(row2, 4 + i), COLORS.OVERTIME, COLORS.TEXT_W, true, 9)
                ws.getCell(row2, 4 + i).value = h
            }
            row2++

            for (data in mData.overtime) {
                ws.getCell(row2, 4).value = data["date"]
                ws.getCell(row2, 5).value = data["start"]
                ws.getCell(row2, 6).value = data["end"]
                ws.getCell(row2, 7).value = data["hours"]
                ws.getCell(row2, 8).value = data["status"]
                row2++
            }
        }

        // ATTENDANCE
        if (mData.attendance.length > 0) {
            styleCell(ws.getCell(row2, 4), COLORS.ATTENDANCE, COLORS.TEXT_W, true)
            ws.getCell(row2, 4).value = "📅 ATTENDANCE"
            row2++

            arrayOf("Date", "In", "Out", "Hours", "Status").forEachIndexed { i, h ->
                styleCell(ws.getCell(row2, 4 + i), COLORS.ATTENDANCE, COLORS.TEXT_W, true, 9)
                ws.getCell(row2, 4 + i).value = h
            }
            row2++

            for (data in mData.attendance) {
                ws.getCell(row2, 4).value = data["date"]
                ws.getCell(row2, 5).value = data["in"]
                ws.getCell(row2, 6).value = data["out"]
                ws.getCell(row2, 7).value = data["hours"]
                ws.getCell(row2, 8).value = data["status"]
                row2++
            }
        }

        val mdEndRow = row2 - 1

        // Merge Name, Email, Role cells
        ws.mergeCells(mdStartRow, 1, mdEndRow, 1)
        ws.mergeCells(mdStartRow, 2, mdEndRow, 2)
        ws.mergeCells(mdStartRow, 3, mdEndRow, 3)

        ws.getCell(mdStartRow, 1).alignment = js("{ horizontal: 'center', vertical: 'middle', wrapText: true }")
        ws.getCell(mdStartRow, 2).alignment = js("{ horizontal: 'center', vertical: 'middle', wrapText: true }")
        ws.getCell(mdStartRow, 3).alignment = js("{ horizontal: 'center', vertical: 'middle', wrapText: true }")

        row2++
    }

    // ===== VERTICAL SEPARATOR (Column J=10) =====
    for (r in 1..row2) {
        ws.getCell(r, 10).border = js("{ right: { style: 'medium', color: '000000' } }")
    }

    // ===== COLUMN WIDTHS =====
    ws.getColumn(1).width = 18
    ws.getColumn(2).width = 25
    ws.getColumn(3).width = 12
    ws.getColumn(4).width = 15
    ws.getColumn(5).width = 15
    ws.getColumn(6).width = 20
    ws.getColumn(7).width = 12
    ws.getColumn(8).width = 12
    ws.getColumn(9).width = 20
    ws.getColumn(10).width = 2
    ws.getColumn(11).width = 2
    ws.getColumn(12).width = 8
    ws.getColumn(13).width = 12
    ws.getColumn(14).width = 15
    ws.getColumn(15).width = 18
    ws.getColumn(16).width = 25
    ws.getColumn(17).width = 12

    for (i in 1..numDays) {
        ws.getColumn(17 + i).width = 4
    }

    // ===== SAVE =====
    workbook.xlsx.writeBuffer().then { buffer: dynamic ->
        val blob = js("new Blob([buffer], { type: 'application/vnd.ms-excel' })")
        val url = js("window.URL.createObjectURL(blob)")
        val a = js("document.createElement('a')")
        a.href = url
        a.download = "shift_schedule_${getMonthName(month)}_${year}.xlsx"
        a.click()
        js("window.URL.revokeObjectURL(url)")
        console.log("✅ Shift schedule Excel exported!")
    }
}

private fun styleCell(cell: dynamic, bg: String?, text: String?, bold: Boolean, size: Int = 10) {
    if (bg != null) {
        val fillObj = js("({})")
        fillObj.type = "pattern"
        fillObj.pattern = "solid"
        val fgColor = js("({})")
        fgColor.argb = bg
        fillObj.fgColor = fgColor
        cell.fill = fillObj
    }
    val fontObj = js("({})")
    val colorObj = js("({})")
    colorObj.argb = text
    fontObj.color = colorObj
    fontObj.bold = bold
    fontObj.size = size
    cell.font = fontObj
    cell.alignment = js("""{ horizontal: 'center', vertical: 'middle', wrapText: true }""")
    cell.border = js("""{ top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }""")
}

private fun getDaysInMonth(year: Int, month: Int): Int {
    return when (month) {
        in 0..7 -> if ((month + 1) == 2) {
            if (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)) 29 else 28
        } else if ((month + 1) in intArrayOf(4, 6, 9, 11)) 30 else 31
        else -> 31
    }
}

private fun getMonthName(month: Int): String {
    val months = arrayOf(
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    )
    return if (month in 0..11) months[month] else "Unknown"
}